// src/containers/contact/index.js
import React, { useState } from 'react';
import { Container, Row, Col, FormGroup } from 'reactstrap';
import { Subtitle, Title } from '../../components/common/title/index';
import Image from '../../components/common/image/index';
import InputBox from '../../components/common/input/index';
import Button from '../../components/common/button';
import ContactBackgroundImages from '../../data/contact/index';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './contact.scss';

const ContactImg = '/assets/images/contact/woman.png';

// busline imported a `sendContactEmail` helper from its own firebase module.
// That module didn't come across, so this calls the callable function directly.
// If the function isn't deployed in this project yet, the message is written to
// a `contactMessages` collection instead so nothing gets dropped on the floor.
async function submitContactMessage(payload) {
  try {
    const call = httpsCallable(getFunctions(), 'sendContactEmail');
    await call(payload);
    return;
  } catch (err) {
    console.warn('sendContactEmail callable unavailable, saving to Firestore:', err);
  }

  await addDoc(collection(db, 'contactMessages'), {
    ...payload,
    status: 'new',
    createdAt: serverTimestamp(),
  });
}

function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const handleSubmit = async () => {
    const error = {};
    if (!name) error.name = "Name field shouldn't be empty";
    if (!email) error.email = "Email field shouldn't be empty";
    if (!message) error.message = "Message field shouldn't be empty";

    if (Object.keys(error).length) {
      setErrors(error);
      return;
    }

    setErrors({});
    setSending(true);
    setOkMsg('');
    setErrMsg('');

    try {
      await submitContactMessage({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        honeypot: '', // keep empty; add a hidden input if you want bot filtering
      });
      setOkMsg('Thanks! Your message has been sent.');
      setName('');
      setEmail('');
      setMessage('');
    } catch (e) {
      console.error(e);
      setErrMsg(e.message || 'Could not send your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-wrapper" id="contact">
      <div className="contact-animation-images">
        {ContactBackgroundImages.map((img, index) => (
          <span key={`contact-img-${index}`} className={`image${index + 1}`}>
            <Image Path={img.img} />
          </span>
        ))}
      </div>
      <Container>
        <Row>
          <Col md={6}>
            <div className="contact-image">
              <Image Path={ContactImg} Class="logo-img" />
            </div>
          </Col>
          <Col md={6}>
            <div className="contact-content-block main-title-wrapper">
              <Subtitle Class="sitemain-subtitle" Name="Contact Us" />

              <p className="contact-intro">
                We're here to help! Whether you have a question, need assistance, or just want to
                share your feedback, our team is ready to connect with you.
              </p>

              <div className="contact-info">
                <ul className="contact-list">
                  <li>
                    <span className="emoji" aria-hidden="true">📩</span>
                    <strong>Email Us</strong>
                    <p>
                      Have a question or request? Email us anytime at{' '}
                      <a href="mailto:team@smartboardlearning.com">team@smartboardlearning.com</a>{' '}
                      and we'll get back to you as soon as possible.
                    </p>
                  </li>

                  <li>
                    <span className="emoji" aria-hidden="true">📞</span>
                    <strong>Call Us</strong>
                    <p>
                      Prefer to talk? Reach our support team at{' '}
                      <a href="tel:5715636142">(571) 563-6142</a> during business hours:
                      <br />
                      <em>Monday – Friday: 9:00 AM – 5:00 PM (EST)</em>
                    </p>
                  </li>

                  <li>
                    <span className="emoji" aria-hidden="true">💬</span>
                    <strong>Message Us</strong>
                    <p>
                      Use the message box on this page to send a quick note — we'll review it and
                      respond promptly.
                    </p>
                  </li>
                </ul>
              </div>

              {/* No <form> element: submission is handled by the button's onClick,
                  which avoids a stray page reload on Enter. */}
              <div className="form">
                <FormGroup>
                  <Title Class="form-label" Name="Name *" />
                  <InputBox
                    Type="text"
                    Name="name"
                    PlaceHolder="John Doe"
                    value={name}
                    ChangeValue={setName}
                    Class={errors.name ? 'error' : ''}
                  />
                </FormGroup>
                <FormGroup>
                  <Title Class="form-label" Name="Email *" />
                  <InputBox
                    Type="text"
                    Name="email"
                    PlaceHolder="example@gmail.com"
                    value={email}
                    ChangeValue={setEmail}
                    Class={errors.email ? 'error' : ''}
                  />
                </FormGroup>
                <FormGroup>
                  <Title Class="form-label" Name="Message *" />
                  <InputBox
                    Type="textarea"
                    Name="text"
                    PlaceHolder="Write your message"
                    Class={`textbox ${errors.message ? 'error' : ''}`}
                    value={message}
                    ChangeValue={setMessage}
                  />
                </FormGroup>
              </div>

              <Button
                Class="button1 btn button2 gradient-color"
                Name={sending ? 'SENDING…' : 'SUBMIT'}
                Clickble={sending ? undefined : handleSubmit}
                BtnIcon="btn-icon"
              />

              {okMsg ? <p className="contact-status ok">{okMsg}</p> : null}
              {errMsg ? <p className="contact-status error">{errMsg}</p> : null}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default Contact;
